import { useEffect, useState } from "react";
import Skeleton from "@mui/material/Skeleton";
import PropTypes from "prop-types";
import { Avatar, Box, Drawer, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import { NavItem } from "./NavItem";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import HelpIcon from "@mui/icons-material/Help";
import SavedSearchIcon from "@mui/icons-material/SavedSearch";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import PausePresentationIcon from "@mui/icons-material/PausePresentation";
import { config } from "../../style";
import { ReactComponent as Logo } from "../../assets/images/logos/logo-wsb.svg";
import { get } from "../../api";
import { DollarFeesIcon } from "../../icons/dollar-fees-icon";
import { useAuth } from "../../hooks/useAuth";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { ReactComponent as CollapseIcon } from "../../assets/images/icons/collapse-icon.svg";
import { ChevronLeft } from "@mui/icons-material";

const clientApplicationItems = (userDetails) => {
    let isBureau = userDetails?.contact?.clientType === "Bureau";
    let records = [
        {
            href: "/",
            id: "my-dashboard",
            icon: <SpaceDashboardIcon fontSize="medium" />,
            title: "My Dashboard",
        },
        {
            href: "/speaker-search",
            id: "speaker-search",
            icon: <SavedSearchIcon fontSize="medium" />,
            title: "Speaker Search",
        },
        {
            href: "/my-fee-requests",
            id: "my-fee-requests",
            icon: <DollarFeesIcon fontSize="medium" />,
            title: "My Speaker Requests",
        },
        {
            href: "/my-proposals",
            id: "my-proposals",
            icon: <CreditCardIcon fontSize="small" />,
            title: "My Proposals",
        },
        {
            href: "/events",
            id: "my-events",
            icon: <LocalActivityIcon fontSize="small" />,
            title: "My Events",
        },
        // {
        //     href: "/calendar",
        //     id: "calendar",
        //     icon: <CalendarMonthIcon fontSize="small" />,
        //     title: "Calendar",
        // },
        // {
        //     href: "/documents",
        //     id: "documents",
        //     icon: <FindInPageIcon fontSize="small" />,
        //     title: "Documents",
        // },
        // {
        //     href: "/contact",
        //     id: "contact",
        //     icon: <ContactSupportOutlinedIcon fontSize="small" />,
        //     title: "Contact WSB",
        // },
        {
            href: "/help",
            id: "help",
            icon: <HelpIcon fontSize="small" />,
            title: "Help",
        },
    ];

    if (!isBureau) {
        records = records.filter((item) => item.id !== "my-fee-requests");
    }

    return records;
};

const speakerApplicationItems = [
    {
        href: "/",
        id: "my-events",
        icon: <LocalActivityIcon fontSize="small" />,
        title: "My Events",
    },
    {
        href: "/speaker-holds",
        id: "speaker-holds",
        icon: <PausePresentationIcon fontSize="small" />,
        title: "Hold Requests",
    },
    {
        href: "/help",
        id: "help",
        icon: <HelpIcon fontSize="small" />,
        title: "Help",
    },
];

export const DashboardSidebar = (props) => {
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    let { open, tab, onClose, publicPage, application, opportunityData, sidebarCollapse, setSidebarCollapse } = props;
    const [userImage, setUserImage] = useState(opportunityData?.opportunity?.ownerPhoto);
    const [showCollapseIcon, setShowCollapseIcon] = useState(false);

    const { getUserDetailsNotAsync } = useAuth();
    const userDetails = getUserDetailsNotAsync();

    const [userImageBase, setUserImageBase] = useState("");
    useEffect(() => {
        setIsImageLoaded(false);
        if (opportunityData?.opportunity?.ownerPhoto) {
            get("opportunity/getUserImage", {
                url: encodeURIComponent(opportunityData?.opportunity?.ownerPhoto),
            }).then(async (result) => {
                if (result.isSuccess) {
                    setUserImageBase(result.data);
                }
                setIsImageLoaded(true);
            });
        } else {
            setIsImageLoaded(true);
        }
    }, [userImage, opportunityData?.opportunity?.ownerPhoto]);

    const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"), {
        defaultMatches: true,
        noSsr: false,
    });

    const getNavBarItems = () => {
        let pages = [];
        if (application === "speaker") {
            pages = publicPage ? speakerApplicationItems.filter((item) => item.id === tab) : speakerApplicationItems;
        } else {
            pages = publicPage ? clientApplicationItems(userDetails).filter((item) => item.id === tab) : clientApplicationItems(userDetails);
        }

        if (!userDetails?.contact?.eventsEnabled) {
            pages = pages.filter((item) => item.id !== "my-events");
        }

        if (!userDetails?.contact?.speakerSearchEnabled) {
            pages = pages.filter((item) => item.id !== "speaker-search");
        }

        return pages;
    };

    useEffect(() => {
        const handleResize = () => {
            if (!lgUp) {
                setSidebarCollapse(false);
                setShowCollapseIcon(false);
            } else {
                setShowCollapseIcon(true);
            }
        };

        // Initial check
        handleResize();
    }, [lgUp]);

    // // testing data
    // opportunityData = {
    //     opportunity: {
    //         ownerName: "Einstein",
    //         ownerEmail: "Einstein@gmail.com",
    //         ownerPhone: 90909090909,
    //     },
    // };

    const BottomImage = (
        <>
            {isImageLoaded && (
                <Avatar
                    src={userImageBase}
                    sx={{
                        height: sidebarCollapse ? 35 : 64,
                        mb: 2,
                        width: sidebarCollapse ? 35 : 64,
                        margin: "auto",
                    }}
                />
            )}
            {!isImageLoaded && <Skeleton variant="circular" width={sidebarCollapse ? 35 : 64} height={sidebarCollapse ? 35 : 64} sx={{ margin: "0px auto", background: "rgba(255, 255, 255, 0.6)" }} />}
        </>
    );
    const BottomImageData = (
        <>
            {sidebarCollapse && BottomImage}
            <Box sx={{ marginTop: "0.5rem", fontSize: "16px", fontWeight: 500 }}>{opportunityData?.opportunity?.ownerName}</Box>
            <Box sx={{ fontSize: "12px" }}>{opportunityData?.opportunity?.ownerTitle ? opportunityData?.opportunity?.ownerTitle : "Proposal Manager"}</Box>
            <Box
                sx={{
                    marginTop: ".5rem",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <MailOutlineIcon sx={{ mr: "10px" }} fontSize="small" />
                {opportunityData?.opportunity?.ownerEmail}
            </Box>
            {opportunityData?.opportunity?.ownerPhone && (
                <Box
                    sx={{
                        marginTop: ".5rem",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <PhoneIphoneOutlinedIcon sx={{ mr: "10px" }} fontSize="small" />
                    {opportunityData?.opportunity?.ownerPhone}
                </Box>
            )}
        </>
    );
    const CollapseButtonIcon = (
        <>
            {showCollapseIcon && (
                <IconButton
                    onClick={() => setSidebarCollapse((prevState) => !prevState)}
                    sx={{
                        width: "30px",
                        height: "30px",
                        transform: sidebarCollapse ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.3s ease",

                        border: "1px solid #21315a",
                        borderRadius: "50%",
                        position: "absolute",
                        top: "28px",
                        right: "-13px",
                        color: "#21315a",
                        background: "#fff",

                        "&:hover": {
                            background: "#fff",
                        },
                    }}
                >
                    <ChevronLeft />
                </IconButton>
            )}
        </>
    );
    const content = (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflowX: sidebarCollapse ? "hidden" : "auto",
                }}
            >
                {!sidebarCollapse && (
                    <div>
                        <Box sx={{ p: sidebarCollapse ? 1 : 3, width: sidebarCollapse ? "80px" : "200px", m: "auto" }}>
                            <Logo />
                        </Box>
                        <Box sx={{ px: 2 }}></Box>
                    </div>
                )}

                <Box
                    sx={{
                        flexGrow: 1,
                        px: sidebarCollapse ? "14px" : "20px",

                        ...(sidebarCollapse && {
                            marginTop: "5rem",
                        }),
                    }}
                >
                    {getNavBarItems().map((item) => (
                        <NavItem key={item.id} tab={tab} icon={item.icon} href={item.href} sidebarCollapse={sidebarCollapse} id={item.id} title={item.title} onClick={onClose} />
                    ))}
                </Box>
                {opportunityData?.opportunity && (!publicPage || application === "preview") ? (
                    <Box sx={{ p: "20px", px: sidebarCollapse ? "14px" : "20px" }}>
                        <Box
                            sx={{
                                textAlign: "center",
                                margin: "auto",
                                py: 2,
                                bgcolor: "rgba(250,250,250, .23)",
                                borderRadius: "4px",
                                px: 1,
                                overflowX: "hidden" 
                            }}
                        >
                            {sidebarCollapse ? (
                                <Tooltip
                                    // open
                                    title={BottomImageData}
                                    arrow
                                    placement="right"
                                    // disableHoverListener={!sidebarCollapse}
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                backgroundColor: "rgba(250,250,250, .23)",
                                                background: "linear-gradient(180deg, rgba(32,51,91,1) 0%, rgba(75,24,74,1) 100%)",
                                                padding: "1rem",
                                                textAlign: "center",
                                            },
                                        },
                                        arrow: {
                                            sx: {
                                                color: "#23315a",
                                            },
                                        },
                                    }}
                                >
                                    <IconButton sx={{ m: 0, p: 0 }}>{BottomImage}</IconButton>
                                </Tooltip>
                            ) : (
                                BottomImage
                            )}

                            {!sidebarCollapse && BottomImageData}
                        </Box>
                    </Box>
                ) : (
                    ""
                )}
            </Box>
        </>
    );

    if (lgUp) {
        return (
            <Drawer
                anchor="left"
                open
                PaperProps={{
                    sx: {
                        background: config.sidebarColor,
                        color: "#FFFFFF",
                        width: sidebarCollapse ? 80 : 280,
                        // overflowX: sidebarCollapse ? "hidden" : "auto",
                        overflow:"unset",
                        zIndex: 100,
                        transition: "width 0.3s ease",
                    },
                }}
                variant="permanent"
            >
                {CollapseButtonIcon}
                {content}
            </Drawer>
        );
    }

    return (
        <Drawer
            anchor="left"
            onClose={onClose}
            open={open}
            PaperProps={{
                sx: {
                    background: config.sidebarColor,
                    color: "#FFFFFF",

                    width: sidebarCollapse ? 80 : 280,
                    // overflowX: sidebarCollapse ? "hidden" : "auto",
                    overflow: "unset",
                    zIndex: 100,
                    transition: "width 0.3s ease",
                },
            }}
            sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
            variant="temporary"
        >
            {content}
        </Drawer>
    );
};

DashboardSidebar.propTypes = {
    onClose: PropTypes.func,
    open: PropTypes.bool,
};
